import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import {
  getCurriculumSubjects,
  getCurriculumSubjectById,
  getCoreTopicsByCurriculumSubject,
  CoreTopicDTO,
} from '../services/adminCourseService';
import adminApi from '../services/adminApi';
import LLMAssistantDialog, { LLMQueryOptions } from '../components/LLMTopicAssistant/LLMAssistantDialog';
import ReviewChangesDialog, { TopicSuggestion } from '../components/LLMTopicAssistant/ReviewChangesDialog';
import { Psychology as PsychologyIcon } from '@mui/icons-material';

const SubjectTopicsPage: React.FC = () => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [subject, setSubject] = useState<{ id: number; code: string; name: string; description?: string } | null>(null);
  const [coreTopics, setCoreTopics] = useState<CoreTopicDTO[]>([]);
  const [expandedTopicIds, setExpandedTopicIds] = useState<Set<number>>(new Set());

  const [addTopicDialogOpen, setAddTopicDialogOpen] = useState(false);
  const [editTopicDialogOpen, setEditTopicDialogOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<CoreTopicDTO | null>(null);
  const [selectedParentTopic, setSelectedParentTopic] = useState<CoreTopicDTO | null>(null);
  const [topicTitle, setTopicTitle] = useState('');
  const [topicDescription, setTopicDescription] = useState('');
  const [parentTopicId, setParentTopicId] = useState<number | null>(null);
  const [savingTopic, setSavingTopic] = useState(false);
  const [isAddingChild, setIsAddingChild] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState<CoreTopicDTO | null>(null);
  const [deletingTopic, setDeletingTopic] = useState(false);

  // LLM Assistant states
  const [llmDialogOpen, setLlmDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmProgress, setLlmProgress] = useState(0);
  const [llmProgressMessage, setLlmProgressMessage] = useState('');
  const [llmSuggestions, setLlmSuggestions] = useState<TopicSuggestion[]>([]);
  const [eoHierarchy, setEOHierarchy] = useState<any[]>([]);
  // Prompt preview / confirmation before calling LLM
  const [promptPreview, setPromptPreview] = useState<string | null>(null);
  const [promptConfirmOpen, setPromptConfirmOpen] = useState(false);
  const [pendingLlmQuery, setPendingLlmQuery] = useState<string>('');
  const [pendingLlmOptions, setPendingLlmOptions] = useState<LLMQueryOptions | null>(null);
  const [promptPreviewLoading, setPromptPreviewLoading] = useState(false);

  useEffect(() => {
    if (subjectId) {
      loadData();
    }
  }, [subjectId]);

  const loadData = async () => {
    if (!subjectId) return;
    try {
      setLoading(true);
      setError(null);

      const subjectIdNum = parseInt(subjectId);
      console.log('Loading subject topics for subject ID:', subjectIdNum);

      // Try to load subject first
      let subject: { id: number; code: string; name: string; description?: string } | null = null;
      try {
        console.log('Attempting to fetch subject by ID:', subjectIdNum);
        subject = await getCurriculumSubjectById(subjectIdNum);
        console.log('Successfully fetched subject:', subject);
      } catch (directErr: any) {
        console.warn('Failed to fetch subject by ID, trying list fallback:', directErr);
        console.warn('Error details:', {
          status: directErr.response?.status,
          message: directErr.response?.data?.message || directErr.message,
          url: directErr.config?.url,
        });

        // Fallback: try to get it from the list
        try {
          const allSubjects = await getCurriculumSubjects();
          console.log('Fetched all subjects:', allSubjects);
          const found = allSubjects.find(s => s.id === subjectIdNum);
          if (found) {
            subject = found;
            console.log('Found subject in list:', subject);
          } else {
            console.error('Subject not found in list. Available subjects:', allSubjects.map(s => ({ id: s.id, code: s.code })));
            throw new Error(`Subject with ID ${subjectIdNum} not found`);
          }
        } catch (fallbackErr: any) {
          console.error('Fallback also failed:', fallbackErr);
          throw new Error(`Failed to load subject: ${fallbackErr.message || 'Subject not found'}`);
        }
      }

      if (!subject) {
        throw new Error('Subject not found');
      }

      // Set subject first so it's available even if topics fail to load
      setSubject(subject);

      // Load topics separately with proper error handling
      console.log('Loading core topics for subject:', subjectIdNum);
      try {
        const topics = await getCoreTopicsByCurriculumSubject(subjectIdNum);
        console.log('Loaded topics:', topics.length);
        setCoreTopics(topics);
      } catch (topicErr: any) {
        console.warn('Failed to load topics:', topicErr);
        // Topics may not exist yet - this is not an error condition
        // Check if it's a "not found" type error or actual error
        const errorMsg = topicErr.response?.data?.message || topicErr.message || '';
        if (errorMsg.toLowerCase().includes('not found') || topicErr.response?.status === 404) {
          // No topics exist yet - this is fine
          console.log('No topics found for subject - this is expected for new subjects');
          setCoreTopics([]);
        } else {
          // Actual error - still show subject but set a warning
          console.error('Error loading topics:', topicErr);
          setCoreTopics([]);
          setError('Could not load topics. You can still add new topics.');
        }
      }

      // Load EO hierarchy (deprecated API, just set empty)
      setEOHierarchy([]);
    } catch (err: any) {
      console.error('Error loading subject:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load subject';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (id: number) => {
    const next = new Set(expandedTopicIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedTopicIds(next);
  };

  const renderTopicTree = (topics: CoreTopicDTO[], level: number = 0): React.ReactNode => {
    return topics.map((topic) => {
      const hasChildren = !!topic.children && topic.children.length > 0;
      const isExpanded = expandedTopicIds.has(topic.id);

      return (
        <Box key={topic.id} sx={{ pl: level * 3, mb: 0.5 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
              cursor: hasChildren ? 'pointer' : 'default',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} flex={1} onClick={() => hasChildren && toggleTopic(topic.id)}>
              {hasChildren ? (
                isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />
              ) : (
                <Box sx={{ width: 24 }} />
              )}
              {hasChildren ? (
                <FolderOpenIcon fontSize="small" color="primary" />
              ) : (
                <FolderIcon fontSize="small" color="disabled" />
              )}
              <Typography variant="body2" fontWeight={500}>
                {topic.title}
              </Typography>
              {topic.description && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  - {topic.description}
                </Typography>
              )}
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedParentTopic(topic);
                  setParentTopicId(topic.id);
                  setTopicTitle('');
                  setTopicDescription('');
                  setSelectedTopic(null);
                  setIsAddingChild(true);
                  setAddTopicDialogOpen(true);
                }}
                title="Add Child Topic"
              >
                <AddIcon fontSize="small" color="primary" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/subjects/${subjectId}/topics/${topic.id}/dashboard`);
                }}
                title="Open Dashboard"
                color="secondary"
              >
                <DashboardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedTopic(topic);
                  setTopicTitle(topic.title);
                  setTopicDescription(topic.description || '');
                  setParentTopicId(topic.parentTopicId || null);
                  setSelectedParentTopic(null);
                  setIsAddingChild(false);
                  setEditTopicDialogOpen(true);
                }}
                title="Edit Topic"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setTopicToDelete(topic);
                  setDeleteDialogOpen(true);
                }}
                title="Delete Topic"
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {hasChildren && isExpanded && renderTopicTree(topic.children || [], level + 1)}
        </Box>
      );
    });
  };

  const handleOpenAddTopicDialog = () => {
    setSelectedTopic(null);
    setSelectedParentTopic(null);
    setTopicTitle('');
    setTopicDescription('');
    setParentTopicId(null);
    setIsAddingChild(false);
    setAddTopicDialogOpen(true);
  };

  const handleSaveTopic = async () => {
    if (!topicTitle.trim()) {
      setError('Topic title is required');
      return;
    }
    if (!subjectId) return;

    try {
      setSavingTopic(true);
      setError(null);

      const payload: any = {
        title: topicTitle.trim(),
        description: topicDescription.trim() || undefined,
      };

      // Only include parentTopicId if we're adding a child topic
      if (isAddingChild && parentTopicId) {
        payload.parentTopicId = parentTopicId;
      } else if (!isAddingChild && !selectedTopic) {
        // Root topic - set to null explicitly
        payload.parentTopicId = null;
      }
      // For edit, don't include parentTopicId - backend will keep existing value

      if (selectedTopic) {
        // Update - use Neo4j endpoint
        await adminApi.put(`/api/admin/graph/topics/${selectedTopic.id}`, payload);
      } else {
        // Create - use Neo4j endpoint
        await adminApi.post(`/api/admin/graph/topics/subject/${subjectId}`, payload);
      }

      await loadData();
      setAddTopicDialogOpen(false);
      setEditTopicDialogOpen(false);
      setSelectedTopic(null);
      setSelectedParentTopic(null);
      setIsAddingChild(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save topic');
    } finally {
      setSavingTopic(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!topicToDelete) return;
    try {
      setDeletingTopic(true);
      setError(null);
      await adminApi.delete(`/api/admin/graph/topics/${topicToDelete.id}`);
      await loadData();
      setDeleteDialogOpen(false);
      setTopicToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete topic:', err);
      setError(err.response?.data?.message || 'Failed to delete topic');
    } finally {
      setDeletingTopic(false);
    }
  };

  const getAllTopicsFlat = (topics: CoreTopicDTO[]): CoreTopicDTO[] => {
    const result: CoreTopicDTO[] = [];
    const traverse = (ts: CoreTopicDTO[]) => {
      ts.forEach(t => {
        result.push(t);
        if (t.children) traverse(t.children);
      });
    };
    traverse(topics);
    return result;
  };

  /** Step 1: Get prompt preview and show for user confirmation */
  const handleLLMQuery = async (query: string, options: LLMQueryOptions) => {
    if (!subjectId) return;

    try {
      setPromptPreviewLoading(true);
      setError(null);
      const res = await adminApi.post<{ data: { prompt: string } }>(
        `/api/admin/graph/topics/subject/${subjectId}/llm-suggestions/preview`,
        { query, options },
        { timeout: 15000 }
      );
      const prompt = res.data?.data?.prompt ?? '';
      setPromptPreview(prompt);
      setPendingLlmQuery(query);
      setPendingLlmOptions(options);
      setPromptConfirmOpen(true);
    } catch (err: any) {
      console.error('Prompt preview failed:', err);
      setError(err.response?.data?.message || 'Failed to load prompt preview');
    } finally {
      setPromptPreviewLoading(false);
    }
  };

  /** Step 2: After user confirms, call LLM and show suggestions */
  const handleConfirmPromptAndRun = async () => {
    if (!subjectId || pendingLlmOptions == null) return;

    const query = pendingLlmQuery;
    const options = pendingLlmOptions;

    try {
      setPromptConfirmOpen(false);
      setPromptPreview(null);
      setLlmLoading(true);
      setLlmProgress(0);
      setLlmProgressMessage('Preparing context...');

      const progressInterval = setInterval(() => {
        setLlmProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      setLlmProgressMessage('Sending request to AI...');

      const response = await adminApi.post(
        `/api/admin/graph/topics/subject/${subjectId}/llm-suggestions`,
        { query, options },
        { timeout: 120000 }
      );

      clearInterval(progressInterval);
      setLlmProgress(100);
      setLlmProgressMessage('Processing response...');

      const responseData = response.data.data;
      const suggestions = responseData.suggestions || [];

      // Map backend response to frontend format
      const mappedSuggestions: TopicSuggestion[] = suggestions.map((s: any) => ({
        id: s.id,
        type: s.type,
        title: s.title,
        description: s.description,
        parentTopicId: s.parentTopicId,
        parentTopicTitle: s.parentTopicTitle,
        prerequisites: s.prerequisites?.map((p: any) => ({
          id: p.id,
          title: p.title,
        })) || [],
        displayOrder: s.displayOrder,
        originalData: s.originalTopicId ? { id: s.originalTopicId } : undefined,
        changes: s.changes ? {
          title: s.changes.title ? {
            old: s.changes.title.old,
            new: s.changes.title.new_ || s.changes.title.new,
          } : undefined,
          description: s.changes.description ? {
            old: s.changes.description.old,
            new: s.changes.description.new_ || s.changes.description.new,
          } : undefined,
          parentTopicId: s.changes.parentTopicId ? {
            old: s.changes.parentTopicId.old,
            new: s.changes.parentTopicId.new_ || s.changes.parentTopicId.new,
          } : undefined,
        } : undefined,
        reason: s.reason,
      }));

      setLlmSuggestions(mappedSuggestions);

      setLlmLoading(false);
      setLlmDialogOpen(false);
      setReviewDialogOpen(true);
    } catch (err: any) {
      console.error('LLM query failed:', err);

      let errorMessage = 'Failed to get AI suggestions';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The AI service is taking too long to respond. Please try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLlmLoading(false);
      // Keep the dialog open so user can see the error and retry
      setLlmDialogOpen(true);
    }
  };

  const handleApplyLLMChanges = async (acceptedSuggestions: TopicSuggestion[]) => {
    if (!subjectId) return;

    try {
      setSavingTopic(true);
      setError(null);

      // Apply changes in order: deletes first, then edits, then adds
      const deletes = acceptedSuggestions.filter(s => s.type === 'delete');
      const edits = acceptedSuggestions.filter(s => s.type === 'edit');
      const adds = acceptedSuggestions.filter(s => s.type === 'add');

      // Apply deletes
      for (const suggestion of deletes) {
        if (suggestion.originalData?.id) {
          await adminApi.delete(`/api/admin/graph/topics/${suggestion.originalData.id}`);
        }
      }

      // Apply edits
      for (const suggestion of edits) {
        if (suggestion.originalData?.id) {
          const edited = suggestion.changes || {};
          await adminApi.put(`/api/admin/graph/topics/${suggestion.originalData.id}`, {
            title: edited.title?.new || suggestion.title,
            description: edited.description?.new || suggestion.description,
            parentTopicId: edited.parentTopicId?.new,
          });
        }
      }

      // Apply adds - need to track created topic IDs for prerequisites
      const createdTopicMap = new Map<string, number>(); // suggestion.id -> created topicId

      // Topological sort for new topics to ensure parents are created before children
      const adsIds = new Set(adds.map(s => s.id));
      const sortedAdds: TopicSuggestion[] = [];
      const pendingAdds = [...adds];

      let stuck = false;
      while (pendingAdds.length > 0 && !stuck) {
        const readyIndex = pendingAdds.findIndex(s => {
          const pId = s.parentTopicId?.toString();
          // Ready if no parent, OR parent is external (not in adds - implies existing topic), 
          // OR parent is already processed/sorted
          if (!pId) return true;
          if (!adsIds.has(pId)) return true; // Parent is not in the new batch, so it must be existing
          return sortedAdds.some(processed => processed.id === pId); // Parent is already sorted
        });

        if (readyIndex !== -1) {
          sortedAdds.push(pendingAdds[readyIndex]);
          pendingAdds.splice(readyIndex, 1);
        } else {
          // Circular dependency or unresolvable parent in new topics
          console.warn("Circular dependency or unresolvable parent in new topics, processing remaining as roots/independent likely to fail if strictly dependent:", pendingAdds);
          sortedAdds.push(...pendingAdds);
          stuck = true;
        }
      }

      for (const suggestion of sortedAdds) {
        const pIdStr = suggestion.parentTopicId?.toString();
        // Resolve parent ID: 
        // 1. Check if it's a newly created topic (in map)
        // 2. If not, checking if it's an existing topic (not in adds set)

        let finalParentId: number | string | null = null;

        if (pIdStr) {
          if (createdTopicMap.has(pIdStr)) {
            finalParentId = createdTopicMap.get(pIdStr)!;
          } else if (!adsIds.has(pIdStr)) {
            // It refers to an existing topic ID (external to this batch)
            finalParentId = suggestion.parentTopicId!;
          } else {
            // It refers to a pending topic in this batch that wasn't created yet (shouldn't happen with sort unless cycle)
            console.error(`Parent topic ${pIdStr} not found for ${suggestion.title}. Creating as root.`);
            finalParentId = null;
          }
        }

        const response = await adminApi.post(`/api/admin/graph/topics/subject/${subjectId}`, {
          title: suggestion.title,
          description: suggestion.description,
          parentTopicId: finalParentId,
          displayOrder: suggestion.displayOrder,
        });

        const createdTopicId = response.data.data.id;
        createdTopicMap.set(suggestion.id, createdTopicId);
      }

      // Create prerequisite relationships after all topics are created
      // This ensures that even if A requires B and A is created first (due to hierarchy),
      // we can link them because B will be created by the time we run this loop.
      for (const suggestion of sortedAdds) {
        if (suggestion.prerequisites && suggestion.prerequisites.length > 0) {
          const sourceTopicId = createdTopicMap.get(suggestion.id);
          if (!sourceTopicId) continue;

          for (const prereq of suggestion.prerequisites) {
            // Use created topic ID if prerequisite was just created, otherwise use original ID
            const prereqIdStr = prereq.id.toString();
            const prereqTopicId = createdTopicMap.get(prereqIdStr) || prereq.id;

            // If prereqTopicId is still a "MNU" string, it means we failed to find the topic (maybe it wasn't created?)
            // We should check if it looks like a Long
            if (typeof prereqTopicId === 'string' && isNaN(Number(prereqTopicId))) {
              console.warn(`Skipping prerequisite ${prereqTopicId} for topic ${sourceTopicId} because it couldn't be resolved to a valid ID.`);
              continue;
            }

            try {
              await adminApi.post(`/api/admin/graph/topics/${sourceTopicId}/prerequisites`, {
                prerequisiteTopicId: prereqTopicId,
                strength: 'REQUIRED',
                isRequired: true,
              });
            } catch (prereqErr: any) {
              console.warn(`Failed to create prerequisite ${prereqTopicId} for topic ${sourceTopicId}:`, prereqErr);
              // Continue with other prerequisites
            }
          }
        }
      }

      // Reload data
      await loadData();
      setReviewDialogOpen(false);
      setLlmSuggestions([]);
    } catch (err: any) {
      console.error('Failed to apply changes:', err);
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;
      setError(msg || 'Failed to apply changes');
    } finally {
      setSavingTopic(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!subject) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Subject not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/subjects')} sx={{ mt: 2 }}>
          Back to Subjects
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/subjects')}
            sx={{ mb: 1 }}
          >
            Back to Subjects
          </Button>
          <Typography variant="h4" component="h1">
            Manage Topics - {subject.code} - {subject.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add, edit, and delete core topics for this curriculum subject.
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<PsychologyIcon />}
            onClick={() => setLlmDialogOpen(true)}
          >
            Get AI Help
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenAddTopicDialog()}
          >
            Add Root Topic
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {coreTopics.length === 0 ? (
          <Box textAlign="center" py={6}>
            <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Topics Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3} maxWidth={400} mx="auto">
              This subject doesn't have any topics defined yet. 
              Start by adding your first topic manually or use AI assistance to generate a topic structure.
            </Typography>
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenAddTopicDialog()}
              >
                Add First Topic
              </Button>
              <Button
                variant="outlined"
                startIcon={<PsychologyIcon />}
                onClick={() => setLlmDialogOpen(true)}
              >
                Generate with AI
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
            {renderTopicTree(coreTopics)}
          </Box>
        )}
      </Paper>

      {/* Add/Edit Topic Dialog */}
      <Dialog
        open={addTopicDialogOpen || editTopicDialogOpen}
        onClose={() => {
          setAddTopicDialogOpen(false);
          setEditTopicDialogOpen(false);
          setSelectedTopic(null);
          setSelectedParentTopic(null);
          setIsAddingChild(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedTopic ? 'Edit Topic' : isAddingChild ? 'Add Child Topic' : 'Add Root Topic'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {isAddingChild && selectedParentTopic && (
              <TextField
                label="Parent Topic"
                fullWidth
                value={selectedParentTopic.title}
                InputProps={{
                  readOnly: true,
                }}
                helperText="This topic will be added as a child of the selected parent topic"
              />
            )}
            {!isAddingChild && !selectedTopic && (
              <Typography variant="body2" color="text.secondary">
                This will be a root topic (no parent).
              </Typography>
            )}
            <TextField
              label="Topic Title"
              fullWidth
              value={topicTitle}
              onChange={(e) => setTopicTitle(e.target.value)}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={topicDescription}
              onChange={(e) => setTopicDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddTopicDialogOpen(false);
            setEditTopicDialogOpen(false);
            setSelectedTopic(null);
            setSelectedParentTopic(null);
            setIsAddingChild(false);
          }}>Cancel</Button>
          <Button
            onClick={handleSaveTopic}
            variant="contained"
            disabled={savingTopic}
          >
            {savingTopic ? 'Saving...' : (selectedTopic ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingTopic && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Topic</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete topic <strong>{topicToDelete?.title}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone. All child topics and associated content will also be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deletingTopic}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTopic}
            color="error"
            variant="contained"
            disabled={deletingTopic}
            autoFocus
          >
            {deletingTopic ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* LLM Assistant Dialog */}
      <LLMAssistantDialog
        open={llmDialogOpen}
        onClose={() => setLlmDialogOpen(false)}
        onSubmit={handleLLMQuery}
        subject={subject!}
        existingTopics={coreTopics}
        eoHierarchy={eoHierarchy}
        loading={llmLoading || promptPreviewLoading}
        progress={llmLoading ? llmProgress : 0}
        progressMessage={promptPreviewLoading ? 'Loading prompt preview...' : llmProgressMessage}
      />

      {/* Prompt confirmation: show final prompt and get user confirmation before calling LLM */}
      <Dialog open={promptConfirmOpen} onClose={() => { setPromptConfirmOpen(false); setPromptPreview(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Confirm prompt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following prompt will be sent to the AI. Review it and click &quot;Confirm &amp; Run&quot; to generate suggestions.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={12}
            maxRows={24}
            value={promptPreview ?? ''}
            InputProps={{ readOnly: true }}
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPromptConfirmOpen(false); setPromptPreview(null); }}>
            Back
          </Button>
          <Button variant="contained" onClick={handleConfirmPromptAndRun} startIcon={<PsychologyIcon />}>
            Confirm &amp; Run
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Changes Dialog */}
      <ReviewChangesDialog
        open={reviewDialogOpen}
        onClose={() => !savingTopic && setReviewDialogOpen(false)}
        onApply={handleApplyLLMChanges}
        suggestions={llmSuggestions}
        existingTopics={coreTopics}
        applying={savingTopic}
      />
    </Container>
  );
};

export default SubjectTopicsPage;

